/*
 * This file is part of glowingblue/orell-articles.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import RequestError from 'flarum/common/utils/RequestError';
import type { ApiPayloadPlural, ApiQueryParamsPlural } from 'flarum/common/Store';
import type Article from './models/Article';
import shopRequestErrorHandler from './utils/shopRequestErrorHandler';

export interface ArticleSearchParams extends ApiQueryParamsPlural {
	q: string;
	/** JSON-encoded filter parameters forwarded to the webservice. */
	params?: string;
}

export const PAGE_SIZE = 100;

/**
 * Loads the page of results that follows the one this was handed out with.
 */
export type ArticleSearchPage = () => Promise<ArticleSearchResult>;

export interface ArticleSearchResult {
	/**
	 * The articles that matched, or `false` when the query returned nothing at all.
	 */
	articles: Article[] | false;

	/**
	 * Whether these articles are the first page of results, as opposed to a page
	 * appended to an earlier one.
	 */
	isFirstPage: boolean;

	/**
	 * Loads the next page, or `false` when this is the last one.
	 */
	nextPage: ArticleSearchPage | false;
}

/**
 * The most recent article search, kept so that navigating from the search dropdown
 * to the articles page does not repeat the request.
 */
export interface CachedArticleSearch extends ArticleSearchResult {
	/**
	 * The query these results belong to.
	 */
	q: string;
}

const CACHE_KEY = 'articleSearch';

export function getCachedArticleSearch(): CachedArticleSearch | undefined {
	return app.cache[CACHE_KEY] as CachedArticleSearch | undefined;
}

export function setCachedArticleSearch(value: CachedArticleSearch): void {
	app.cache[CACHE_KEY] = value;
}

export default async function search(
	q: string = '',
	params: ReadonlyArray<unknown> = [],
	offset: number = 0,
	limit?: number
): Promise<ArticleSearchResult> {
	const page: { offset: number; limit?: number } = { offset };
	if (limit) {
		page.limit = limit;
	}

	const query: ArticleSearchParams = { q, page };
	if (params.length) {
		query.params = JSON.stringify(params);
	}

	let results;
	try {
		// `store.find` has no overload taking query params and request options for a collection.
		const payload = await app.request<ApiPayloadPlural>({
			method: 'GET',
			url: `${app.forum.attribute('apiUrl')}/of_articles`,
			params: query,
			errorHandler: shopRequestErrorHandler,
		});
		results = app.store.pushPayload<Article[]>(payload);
	} catch (error) {
		// The handler has alerted; the search UI must still settle, so render "no results".
		if (error instanceof RequestError) {
			return { articles: false, isFirstPage: true, nextPage: false };
		}

		throw error;
	}

	if (!results.length) {
		return { articles: false, isFirstPage: true, nextPage: false };
	}

	const links = results.payload.links;
	const nextOffset = links?.next ? decodeURIComponent(links.next).match(/&page\[offset\]=([0-9]+)/) : null;

	return {
		articles: results,
		isFirstPage: !links?.prev,
		nextPage: nextOffset ? () => search(q, params, Number(nextOffset[1]), limit) : false,
	};
}
